import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import DashboardEmptyState from "../components/DashboardEmptyState";
import { dashboardTheme } from "../theme/dashboardTheme";
import { IPropertyAnalyticsDailyPoint } from "../types/ownerDashboard.types";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

interface PropertyAnalyticsLineChartProps {
  dailySeries: IPropertyAnalyticsDailyPoint[];
}

function formatDateLabel(dateString: string): string {
  const [month, day] = dateString.split("/");

  if (!month || !day) {
    return dateString;
  }

  return `${day}/${month}`;
}

export default function PropertyAnalyticsLineChart({
  dailySeries,
}: PropertyAnalyticsLineChartProps) {
  const chartData = useMemo(
    () => ({
      labels: dailySeries.map((point) => formatDateLabel(point.date)),
      datasets: [
        {
          data: dailySeries.map((point) => point.screen_page_views),
          borderColor: dashboardTheme.colors.chartLine,
          backgroundColor: (context: {
            chart: { ctx: CanvasRenderingContext2D; chartArea?: { top: number; bottom: number } };
          }) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return dashboardTheme.colors.chartFill;
            }

            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, "rgba(110, 168, 255, 0.35)");
            gradient.addColorStop(1, "rgba(110, 168, 255, 0.02)");
            return gradient;
          },
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: dashboardTheme.colors.chartLine,
          pointHoverBorderColor: "#f0f6fc",
          pointHoverBorderWidth: 2,
          borderWidth: 2,
        },
      ],
    }),
    [dailySeries]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: dashboardTheme.colors.surfaceElevated,
          borderColor: dashboardTheme.colors.cardBorder,
          borderWidth: 1,
          titleColor: dashboardTheme.colors.textPrimary,
          bodyColor: dashboardTheme.colors.textSecondary,
          callbacks: {
            label: (context: { parsed: { y: number | null } }) =>
              `Visualizações: ${context.parsed.y ?? 0}`,
          },
        },
      },
      scales: {
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            font: { size: 10 },
          },
        },
        y: {
          border: { display: false },
          grid: { color: dashboardTheme.colors.chartGrid, drawTicks: false },
          ticks: {
            color: dashboardTheme.colors.chartTick,
            padding: 8,
            precision: 0,
          },
        },
      },
    }),
    []
  );

  if (dailySeries.length === 0) {
    return (
      <DashboardEmptyState
        align="center"
        title="Sem visualizações no período"
        description="Não há visitas às páginas dos imóveis no intervalo selecionado."
        className="flex h-[220px] items-center justify-center"
      />
    );
  }

  return (
    <div className="h-[clamp(220px,38vh,320px)] w-full min-w-0">
      <Line data={chartData} options={options} />
    </div>
  );
}
